import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { getDeviceId } from "@/lib/deviceId";
import { toast } from "sonner";
import { Delete, ShieldCheck, QrCode, Keyboard, X } from "lucide-react";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "back"];

/**
 * Presented when ``/api/auth/login`` refuses a device with the
 * ``device_not_trusted`` code. Two entry modes:
 *   (a) manual keypad — the operator types the 8-digit approval code
 *       the manager just generated from Settings → Συσκευές.
 *   (b) QR scan — the manager's code panel shows a QR that encodes
 *       ``{code, label}``; scanning it prefills the keypad.
 * After a successful redeem the parent restarts the login flow.
 */
export default function DeviceApprovalScreen({ payload, onApproved, onCancel }) {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("pin"); // "pin" | "qr"

  const submit = async (val) => {
    const finalCode = (val ?? code).trim();
    if (finalCode.length !== 8) {
      toast.error("Ο κωδικός πρέπει να είναι 8 ψηφία");
      return;
    }
    setBusy(true);
    try {
      await http.post("/devices/gate-redeem", {
        tenant_code:
          (payload?.tenantCode || localStorage.getItem("tenant_code") || "").trim().toUpperCase() ||
          undefined,
        code: finalCode,
        device_id: getDeviceId(),
        label: label.trim() || "Συσκευή",
      });
      toast.success("Συσκευή εγκεκριμένη — πληκτρολόγησε το PIN σου");
      onApproved();
    } catch (e) {
      const msg = e.response?.data?.detail || "Άκυρος κωδικός";
      toast.error(msg);
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const press = (k) => {
    if (busy) return;
    if (k === "C") return setCode("");
    if (k === "back") return setCode((v) => v.slice(0, -1));
    const next = (code + k).slice(0, 8);
    setCode(next);
    if (next.length === 8) submit(next);
  };

  return (
    <div className="h-screen w-screen grid place-items-center bg-gradient-to-br from-amber-900 via-slate-900 to-slate-900">
      <div className="w-[460px] rounded-2xl shadow-2xl bg-white p-6" data-testid="device-approval-screen">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 grid place-items-center">
            <ShieldCheck className="text-amber-700" size={26} />
          </div>
          <div className="flex-1">
            <div className="text-lg font-bold text-slate-800">Μη εγκεκριμένη συσκευή</div>
            <div className="text-xs text-slate-500">
              {payload?.message || "Απαιτείται κωδικός έγκρισης από manager"}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            data-testid="device-approval-cancel"
            className="p-1 rounded-md text-slate-400 hover:bg-slate-100"
            aria-label="Ακύρωση"
          >
            <X size={20} />
          </button>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 mb-3 text-[11px] text-slate-600 font-mono break-all">
          ID: {getDeviceId()}
        </div>

        <div className="flex gap-1 mb-3">
          <button
            type="button"
            onClick={() => setMode("pin")}
            data-testid="device-approval-mode-pin"
            className={`flex-1 h-9 rounded-md text-sm font-bold flex items-center justify-center gap-1.5 ${
              mode === "pin" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600"
            }`}
          >
            <Keyboard size={14} /> Κωδικός
          </button>
          <button
            type="button"
            onClick={() => setMode("qr")}
            data-testid="device-approval-mode-qr"
            className={`flex-1 h-9 rounded-md text-sm font-bold flex items-center justify-center gap-1.5 ${
              mode === "qr" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600"
            }`}
          >
            <QrCode size={14} /> QR
          </button>
        </div>

        {mode === "pin" ? (
          <>
            <div className="rounded-xl bg-slate-100 px-4 py-3 mb-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                Κωδικός Έγκρισης (8 ψηφία)
              </div>
              <div
                className="h-10 flex items-center justify-center text-3xl font-mono tabular tracking-[0.35em] font-bold"
                data-testid="device-approval-code-display"
              >
                {code
                  ? code.padEnd(8, "•").split("").map((c, i) => (
                      <span key={i} className={i < code.length ? "text-slate-800" : "text-slate-300"}>
                        {c}
                      </span>
                    ))
                  : <span className="text-slate-400 text-base font-normal tracking-normal">••••••••</span>}
              </div>
            </div>

            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value.slice(0, 40))}
              placeholder="Όνομα συσκευής (π.χ. Ταμείο Α)"
              data-testid="device-approval-label"
              className="w-full h-9 mb-3 px-3 rounded-md bg-white border border-slate-300 text-sm outline-none focus:border-amber-500"
            />

            <div className="grid grid-cols-3 gap-2">
              {KEYS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => press(k)}
                  disabled={busy}
                  data-testid={`device-approval-key-${k}`}
                  className={`h-12 rounded-lg border-2 text-xl font-bold font-mono active:scale-[0.96] transition-all ${
                    k === "C"
                      ? "bg-red-600 border-red-700 text-white hover:bg-red-700"
                      : k === "back"
                      ? "bg-slate-200 border-slate-300 hover:bg-slate-300"
                      : "bg-white border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {k === "back" ? <Delete size={20} className="mx-auto" /> : k}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => submit()}
              disabled={busy || code.length !== 8}
              data-testid="device-approval-submit"
              className="w-full mt-3 h-11 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-bold disabled:bg-slate-300"
            >
              {busy ? "Έγκριση..." : "Έγκριση Συσκευής"}
            </button>
          </>
        ) : (
          <QrScanBlock
            onScan={(scanned) => {
              // Codes may arrive as either 8-digit strings or JSON:
              //   {"code":"12345678","label":"Ταμείο Α"}
              let c = "";
              let l = "";
              try {
                const j = JSON.parse(scanned);
                c = String(j.code || "").replace(/\D/g, "");
                l = j.label || "";
              } catch {
                c = String(scanned || "").replace(/\D/g, "");
              }
              if (c.length === 8) {
                setCode(c);
                if (l) setLabel(l);
                setMode("pin");
                setTimeout(() => submit(c), 150);
              } else {
                toast.error("Το QR δεν περιέχει έγκυρο 8-ψήφιο κωδικό");
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

function QrScanBlock({ onScan }) {
  const ref = useRef(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let scanner;
    let cancelled = false;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled || !ref.current) return;
        scanner = new Html5Qrcode(ref.current.id);
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (decoded) => {
            onScan(decoded);
            scanner.stop().catch(() => {});
          },
          () => { /* ignore per-frame errors */ }
        );
      } catch (e) {
        setError(e?.message || "Δεν είναι διαθέσιμη η κάμερα");
      }
    })();
    return () => {
      cancelled = true;
      try { scanner?.stop?.().catch(() => {}); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="rounded-xl bg-slate-100 border border-slate-200 p-3">
      {error ? (
        <div className="text-center text-sm text-rose-600 py-8">
          {error}<br />
          <span className="text-xs text-slate-500">Χρησιμοποίησε την επιλογή «Κωδικός»</span>
        </div>
      ) : (
        <>
          <div
            id="qr-scan-block"
            ref={ref}
            data-testid="device-approval-qr-block"
            className="w-full h-56 rounded-md overflow-hidden bg-black"
          />
          <div className="text-[11px] text-slate-500 text-center mt-2">
            Σκάναρε τον QR από την οθόνη του manager
          </div>
        </>
      )}
    </div>
  );
}
